<template>
  <div
    class="card card-action card-loc"
    @click.exact="openShopPageInCurrentTab"
    @click.meta.exact="openShopPageInNewTab"
  >
    <div class="card-image">
      <img
        alt="Image of location"
        class="cover-img lazyload"
        :data-src="getImage"
      />
    </div>
    <div class="card-content">
      <div class="cert-logo">
        <span
          v-if="isCertifiedPastureForLife()"
          class="pfla-logo"
          title="Certified Pasture For Life"
        ></span>
        <span
          v-if="isCertifiedOrganic()"
          class="organic-logo"
          title="Certified Organic"
        ></span>
      </div>
      <p class="loc-type type-shop" v-if="item.Type === 'BUTCHER'">
        <i class="fad fa-meat"></i> Butcher
      </p>
      <p class="loc-type type-farm" v-else><i class="fad fa-cow"></i> Farm</p>
      <h5 class="card-title">{{ item.DisplayName }}</h5>
      <p class="type font-small">{{ getOpeningHours }}</p>
      <p class="meta font-small">{{ getAddress }}</p>
      <p class="distance font-small" v-if="distanceLoaded">
        <i class="fad fa-map-marker-alt mr-8"></i>
        {{ `${getDistance} km` }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/global-styles/variables.scss";

.card {
  background-color: $white;
  border-radius: $border-radius;
  box-shadow: $box-shadow;
  position: relative;
  text-align: left;
}

.card-action {
  transition: $transition;
  cursor: pointer;

  &:hover {
    background: $white - 10;
    box-shadow: $box-shadow-hover;
    cursor: pointer;
    transition: $transition;
  }
}

.card-content {
  padding: $spacing-5;
  position: relative;

  h1,
  h2,
  h3,
  h4 {
    margin-bottom: $spacing-1;
  }

  p {
    margin: 0 0 $spacing-2;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
}

.card-title {
  font-family: $body-font-family;
  font-weight: $bold;
  margin-bottom: $spacing-2;
  padding: 0;

  a {
    display: block;
  }

  @media only screen and (max-width: ($medium-bp - 1)) {
    font-size: $font-size-5;
  }
}

.card-image {
  background: $grey-4;
  border-radius: $border-radius $border-radius 0 0;
  height: 8em;
  overflow: hidden;
  position: relative;
  width: 100%;

  img {
    border-radius: $border-radius $border-radius 0 0;
    margin: auto;
  }

  @media screen and (min-width: $small-bp) {
    height: 10em;
  }
}

.card-loc {
  line-height: $line-height;

  p {
    &:last-of-type {
      margin: 0;
    }
  }

  .distance {
    color: $slate;
    margin-bottom: 0;

    svg {
      margin-right: $spacing-1;
    }
  }

  .cert-logo {
    float: right;
    margin: -$spacing-2 0 0;
  }

  .card-content {
    padding: $spacing-4;

    .card-title {
      clear: both;
    }

    .loc-type {
      float: left;
    }
  }
}

.cards-grid {
  .card {
    height: 100%;
  }
}
</style>

<script>
import config from "../../src/config";
// import types from '../../helpers/types.js'
export default {
  name: "Card",
  props: {
    item: Object,
    distanceLoaded: Boolean,
  },
  computed: {
    getDistance() {
      return this.item.Distance;
    },
    getAddress() {
      const jsonObject = JSON.parse(this.item.Address);
      return jsonObject.DisplayAddress;
    },
    getPostCode() {
      const jsonObject = JSON.parse(this.item.Address);
      return jsonObject.PostCode;
    },
    getOpeningHours() {
      if (!this.item.OpeningHours) {
        return `Contact for opening hours`;
      }
      const jsonObject = JSON.parse(this.item.OpeningHours);
      const now = new Date(Date.now());
      var currentDay = now.getDay();
      if (currentDay === 0) {
        currentDay = 6; // Sunday
      } else {
        currentDay = currentDay - 1;
      }
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentHourMinutes = currentHour + "" + currentMinutes;
      const splitted = jsonObject.periods[currentDay].split("-");
      const openFrom = splitted[0];
      const openUntil = splitted[1];

      // 1. We check if currently opened
      if (openFrom == "0000") {
        return `Closed today`;
      }

      // 2. We calculate whether or not the current time is within the opening window
      var openFromHour = openFrom[0] + openFrom[1];
      var openFromMinutes = openFrom[2] + openFrom[3];
      var openUntilHour = openUntil[0] + openUntil[1];
      var openUntilMinutes = openUntil[2] + openUntil[3];

      if (currentHourMinutes < openFrom) {
        return `Opens at ${openFromHour}:${openFromMinutes} today`;
      } else if (
        currentHourMinutes >= openFrom &&
        currentHourMinutes < openUntil
      ) {
        return `Open until ${openUntilHour}:${openUntilMinutes}`;
      } else if (currentHourMinutes > openUntil) {
        return `Closed`;
      }

      return `Open from ${openFromHour}:${openFromMinutes} to ${openUntilHour}:${openUntilMinutes} today`;
    },
    getImage() {
      return (
        config.storage.endpoint +
        this.item.CoverImageURL.replace("cover.jpg", "card.jpg")
      ); // TODO: To be removed when we have a `CardImage` field, see https://trello.com/c/3wy7M9nW
    },
    getUrl() {
      const name = this.item.Name;
      return `/${name}`;
    },
  },
  methods: {
    openShopPageInCurrentTab() {
      this.$router.push(this.getUrl);
    },
    openShopPageInNewTab() {
      window.open(this.getUrl);
    },
    formatUrl(string, operator) {
      // move to global
      return string.toLowerCase().replace(/ /g, operator);
    },
    isCertifiedPastureForLife() {
      if (this.item.Flags === undefined) {
        return false;
      }

      return this.item.Flags.includes("pasture_for_life");
    },
    isCertifiedOrganic() {
      if (this.item.Flags === undefined) {
        return false;
      }
      
      return this.item.Flags.includes("organic");
    },
  },
};
</script>
